import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { QrCode } from '../../components/Receipt.tsx';
import { Button, Confirm, EmptyState, Skeleton } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action, request } from '../../lib/api.ts';
import { dateTime, minutesSince } from '../../lib/format.ts';
import { usePlaces } from '../../lib/places.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface Phone {
  id: string;
  user_name: string;
  platform: 'android' | 'ios';
  model: string | null;
  os_version: string | null;
  app_version: string;
  alerts: boolean;
  scope: 'mine' | 'location';
  location: string | null;
  last_seen: string;
}
interface AppInfo {
  available: boolean;
  version?: string;
  size?: number;
}

/** Newer than: "1.2.0" > "1.10.0" is false. */
export const olderThan = (a: string, b: string) => {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) < (pb[i] ?? 0);
  }
  return false;
};

/** Manage → Phones: how a waiter gets the app onto their phone, and which phones are connected. */
export function Phones() {
  const { data, reload } = useAction<Phone[]>('mobile.devices');
  const [app, setApp] = useState<AppInfo | null>(null);
  useEffect(() => {
    void request<AppInfo>('GET', '/api/mobile/android').then(setApp, () => setApp({ available: false }));
  }, []);
  return (
    <ManageLayout title={t('manage.phones')} subtitle={t('phones.subtitle')}>
      <div className="phones-page">
        <Connect app={app} />
        <section className="card" aria-labelledby="phones-list">
          <header className="card-head">
            <div>
              <h2 id="phones-list">{t('phones.list_title')}</h2>
              <span className="muted">{t('phones.list_hint')}</span>
            </div>
          </header>
          {!data ? (
            <Skeleton h={160} r="var(--radius-md)" />
          ) : data.length === 0 ? (
            <EmptyState icon="phone" title={t('phones.none')} body={t('phones.none_body')} />
          ) : (
            <ul className="phones-list">
              {data.map((p) => (
                <PhoneRow key={p.id} phone={p} latest={app?.available ? app.version ?? null : null} onRemoved={reload} />
              ))}
            </ul>
          )}
          <p className="callout callout-info phones-lost">
            <Icon name="lock" size={16} /> {t('phones.lost')}
          </p>
        </section>
      </div>
    </ManageLayout>
  );
}

function Connect({ app }: { app: AppInfo | null }) {
  const { toast } = useUi();
  const url = `${window.location.origin}/get-app`;
  const [qr, setQr] = useState<string[] | null>(null);
  useEffect(() => {
    void action<{ qr: string[] }>('mobile.connect', { url }).then((r) => setQr(r.qr), () => setQr(null));
  }, [url]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast(t('phones.copied'));
    } catch {
      window.prompt(t('onlineset.copy_by_hand'), window.location.origin);
    }
  };
  return (
    <section className="card phones-connect" aria-labelledby="phones-connect">
      <div className="phones-qr">{qr ? <QrCode rows={qr} label={t('phones.qr_label')} /> : <Skeleton h={168} w={168} />}</div>
      <div className="phones-steps">
        <h2 id="phones-connect">{t('phones.connect_title')}</h2>
        <ol>
          <li>{t('phones.step_scan')}</li>
          <li>{t('phones.step_install')}</li>
          <li>{t('phones.step_sign_in')}</li>
        </ol>
        <p className="muted">{t('phones.or_type')}</p>
        <div className="phones-address">
          <code>{window.location.origin.replace(/^https?:\/\//, '')}</code>
          <Button size="sm" icon="copy" onClick={() => void copy()}>
            {t('phones.copy')}
          </Button>
        </div>
        {app && !app.available ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} /> {t('phones.no_apk')}
          </p>
        ) : app?.available ? (
          <p className="muted phones-version">{t('phones.apk_version', { version: app.version ?? '', size: Math.max(1, Math.round((app.size ?? 0) / 1024)) })}</p>
        ) : null}
        <p className="muted phones-iphone">
          <Icon name="phone" size={16} /> {t('phones.iphone')}
        </p>
      </div>
    </section>
  );
}

/** When the phone last opened the app: minutes, hours, then the date. */
function seen(at: string) {
  const mins = minutesSince(at);
  if (mins < 2) return t('phones.seen_now');
  if (mins < 60) return t('phones.seen_minutes', { n: mins });
  if (mins < 24 * 60) return t('phones.seen_hours', { n: Math.floor(mins / 60) });
  return t('phones.seen_on', { when: dateTime(at) });
}

function PhoneRow({ phone: p, latest, onRemoved }: { phone: Phone; latest: string | null; onRemoved: () => void }) {
  const { name, multi } = usePlaces();
  const { busy, run } = useSave();
  const [asking, setAsking] = useState(false);
  const stale = Date.now() - Date.parse(p.last_seen) > 14 * 86_400_000;
  const remove = () =>
    void run(async () => {
      await action('mobile.forget', { id: p.id });
      setAsking(false);
      onRemoved();
    }, t('phones.removed'));
  return (
    <li className="phones-row">
      <span className="phones-icon" aria-hidden="true">
        <Icon name="phone" size={20} />
      </span>
      <div className="phones-who">
        <strong>{p.user_name}</strong>
        <span className="muted">
          {[p.model, p.os_version ? t('phones.android', { v: p.os_version }) : null].filter(Boolean).join(' · ')}
        </span>
      </div>
      <div className="badge-row">
        {p.alerts ? (
          <span className="badge badge-ok">
            <Icon name="bell" size={14} /> {p.scope === 'location' ? t('phones.alerts_all') : t('phones.alerts_mine')}
            {multi && p.location ? ` · ${name(p.location)}` : ''}
          </span>
        ) : (
          <span className="badge badge-neutral">{t('phones.alerts_off')}</span>
        )}
        {latest && olderThan(p.app_version, latest) ? (
          <span className="badge badge-warn" title={t('phones.update_hint', { version: latest })}>
            {t('phones.update', { version: p.app_version })}
          </span>
        ) : (
          <span className="badge badge-neutral">{t('phones.version', { version: p.app_version })}</span>
        )}
      </div>
      <span className={`phones-seen${stale ? ' text-warn' : ' muted'}`} title={dateTime(p.last_seen)}>
        {seen(p.last_seen)}
      </span>
      <Button variant="ghost" size="sm" icon="trash" onClick={() => setAsking(true)}>
        {t('phones.remove')}
      </Button>
      {asking ? (
        <Confirm
          title={t('phones.remove_title')}
          body={t('phones.remove_body', { name: p.user_name })}
          confirm={t('phones.remove')}
          danger
          busy={busy}
          onConfirm={remove}
          onClose={() => setAsking(false)}
        />
      ) : null}
    </li>
  );
}
